import type {
  ApiRequestConfig,
  BillingScheduleCategory,
  BillingScheduleLine,
  BillingScheduleLinesResponse,
  GenerateBillingScheduleInvoice,
  GenerateBillingScheduleInvoiceResult,
  UpdateBillingScheduleCategory,
  UpdateBillingScheduleLine,
  UpsertBillingScheduleCategory,
  UpsertBillingScheduleLine,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

class BillingScheduleLineApiService extends BaseApi<
  BillingScheduleLine,
  UpsertBillingScheduleLine,
  UpdateBillingScheduleLine
> {
  constructor() {
    super({
      baseEndpoint: '/billing-schedule-lines',
      responseNormalization: true,
    })
  }

  listLines(
    params: {
      entityId?: string
      projectId?: string
      contractId?: string
      billingAuthorizationId?: string
      categoryId?: string
      status?: string | string[]
      billingMonth?: string
      fromDate?: string
      toDate?: string
      search?: string
      page?: number
      limit?: number
    } = {},
    options: Pick<ApiRequestConfig, 'signal' | 'headers'> = {},
  ): Promise<BillingScheduleLinesResponse> {
    const { status, ...rest } = params
    const normalized: Record<string, unknown> = { ...rest }
    if (Array.isArray(status)) {
      if (status.length > 0) normalized.status = status.join(',')
    } else if (status) {
      normalized.status = status
    }
    const query = this.buildQueryParams(normalized)
    return this.makeRequest<BillingScheduleLinesResponse>(
      `${this.config.baseEndpoint}${query}`,
      options,
    )
  }

  generateInvoice(
    data: GenerateBillingScheduleInvoice,
  ): Promise<GenerateBillingScheduleInvoiceResult> {
    return this.makeRequest<GenerateBillingScheduleInvoiceResult>(
      `${this.config.baseEndpoint}/generate-invoice`,
      {
        method: 'POST',
        body: data,
      },
    )
  }
}

class BillingScheduleCategoryApiService extends BaseApi<
  BillingScheduleCategory,
  UpsertBillingScheduleCategory,
  UpdateBillingScheduleCategory
> {
  constructor() {
    super({
      baseEndpoint: '/billing-schedule-categories',
      responseNormalization: true,
    })
  }

  listCategories(
    options: Pick<ApiRequestConfig, 'signal' | 'headers'> = {},
  ): Promise<BillingScheduleCategory[]> {
    return this.makeRequest<BillingScheduleCategory[]>(
      this.config.baseEndpoint,
      options,
    )
  }
}

export const billingScheduleLineApi = createSingletonApi(
  BillingScheduleLineApiService,
)

export const billingScheduleCategoryApi = createSingletonApi(
  BillingScheduleCategoryApiService,
)
